import { AfterViewInit, Directive, ElementRef, HostListener, Input } from '@angular/core';
import { Store } from '@ngrx/store';

import { GA4EventType } from '../analytics/GA4/models/general.model';
import { Ga4Event } from '../analytics/GA4/store/ga4-analytics.actions';

import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { ContentItemTree } from '@features/cms-render/domain/models/contentItemTree';
import { Link } from '@features/cms-render/domain/models/link';
import { AnalyticsClickSourceID } from '@shared/analytics/COMMON/models/analytics-common.models';
import { ObservedElementClick } from '@shared/analytics/store/app-events-analytics.actions';
import { ProductViewPlace } from '@shared/product/models';
import { ProductViewed } from '@shared/product/related-products/infrastructure/state/related-products.actions';

const CMS_SLUGS_WITH_TRACKING = ['massimo-dutti-feel'];

/**
 * Conditions for each type of observed element
 */
export const IS_OBSERVABLE_CONDITONS = {
    isPromotion: (link: Link) => !!link?.categoryId && !['external', 'product'].includes(link?.type),
    isProductView: (place: ProductViewPlace) => !!place,
    isCmsLinkTracked: (link: Link, slug: string) => link?.section && CMS_SLUGS_WITH_TRACKING.includes(slug),
    isAnalyticsClickEvent: (clickId: AnalyticsClickSourceID) => Object.values(AnalyticsClickSourceID).includes(clickId),
};

export interface ElementData {
    link?: Link;
    contentItemTree?: ContentItemTree;
    elementReference?: string;
    $event?: Event;
    observedData?: Record<string, any>;
}

@Directive({
    selector: '[elementObserverDirective]',
    standalone: true,
    providers: [CorePlatformService],
})
export class ElementObserverDirective implements AfterViewInit {
    @Input() public observeType: string;

    @Input() public observeLink: Link;

    @Input() public observedData: Record<string, any>;

    private observer: IntersectionObserver;

    private elementReference: string;

    private readonly config = {
        standardViewTreshold: [0.80, 1], // El elemento se ve por lo menos un 80%
    };

    constructor(
        private readonly store: Store,
        private readonly elementRef: ElementRef,
        private readonly platform: CorePlatformService,
    ) { }

    public ngAfterViewInit(): void {
        // ERROR RuntimeError: NG0205 at R3Injector.assertNotDestroyed
        if (this.platform.isBrowser()) {
            this.setElementReference();

            if (this.isObservableElement(this.observeLink, this.observedData).view) {
                this.createObserver(this.observeType, this.observeLink, this.elementReference, this.observedData);
            }
        }
    }

    private isObservableElement(link: Link, observedData: Record<string, any>): { click: boolean; view: boolean } {
        if (!this.platform.isBrowser()) {
            return;
        }
        // Añadir más condiciones si se quieren observar más tipos de elementos
        // PROMOTION
        if (IS_OBSERVABLE_CONDITONS.isPromotion(link)) return { click: true, view: true };
        // PRODUCT VIEW
        if (IS_OBSERVABLE_CONDITONS.isProductView(observedData?.place)) return { click: true, view: true };
        // CMS TRACKED LINK
        if (IS_OBSERVABLE_CONDITONS.isCmsLinkTracked(link, observedData?.slug)) return { click: true, view: false };
        // ANALYTICS CLICK EVENTS
        if (IS_OBSERVABLE_CONDITONS.isAnalyticsClickEvent(observedData?.clickId)) return { click: true, view: false };

        return { click: false, view: false };
    }

    @HostListener('click', ['$event'])
    private clickCallback($event: Event): void {
        if (this.isObservableElement(this.observeLink, this.observedData).click) {
            this.store.dispatch(ObservedElementClick({
                $event,
                elementReference: this.elementReference,
                link: this.observeLink,
                observedData: this.observedData,
                observeType: this.observeType,
            }));
        }
    }

    private setElementReference(): void {
        this.elementReference = Math.random().toString();
        this.elementRef.nativeElement.setAttribute('data-reference', `${this.elementReference}`); // Para poder obtener el elemento desde la lógica abstracta de analítica
    }

    private getThreshold(overrideConfig?): number[] {
        return overrideConfig?.standardViewTreshold || this.config.standardViewTreshold;
    }

    private createObserver(
        type: string,
        link: Link,
        elementReference: string,
        observedData: Record<string, any>,
        overrideConfig?: Record<string, any>
    ): void {
        const elementData = { link, elementReference, observedData };
        this.observer = new IntersectionObserver((entries, obs) => {
            this.elementViewed(entries, obs, this.elementRef.nativeElement, type, elementData);
        }, { threshold: [...this.getThreshold(overrideConfig)], rootMargin: overrideConfig?.rootMargin });
        this.observer.observe(this.elementRef.nativeElement);
    }

    private elementViewed(
        entries: IntersectionObserverEntry[],
        obs: IntersectionObserver,
        nativeElement: Element,
        type: string,
        data: ElementData
    ): void {
        if (entries[0].isIntersecting) {
            switch (type) {
                case 'promotion':
                    this.viewPromotion(data);
                    break;
                case 'related-product-view':
                    this.relatedProductView(data.observedData);
                    break;
                // Otros tipos de elementos a observar
            }
            obs.unobserve(nativeElement);
        }
    }

    private viewPromotion(data: ElementData): void {
        if (!IS_OBSERVABLE_CONDITONS.isPromotion(data?.link)) return;
        this.store.dispatch(Ga4Event({
            name: GA4EventType.view_promotion,
            data: {
                elementData: {
                    link: data.link,
                    elementReference: data.elementReference,
                    $event: data?.$event,
                },
            },
        }));
    }

    private relatedProductView(observedData: Record<string, any>): void {
        this.store.dispatch(ProductViewed({
            product: null, placeName: observedData?.place, location: observedData?.location,
        }));
    }
}
